// ============================================================================
// Facebook Messenger — outbound message / typing / read-receipt helpers.
//
// Every call targets `graph.facebook.com/v25.0/me/messages` with the page
// access token as the `access_token` query param (per FB docs — do NOT send
// as a Bearer header).
//
// The three `sender_action` calls (typing_on, typing_off, mark_seen) are
// ADVISORY: a failed typing indicator must NOT tank the conversation. We
// therefore swallow + log non-2xx + network errors for those flows and only
// propagate INVALID_INPUT (programmer bug on our side).
//
// The main `sendMessage` function is strict: we throw `FacebookMessengerError`
// on any non-2xx so the dispatcher can react (retry / disable instance).
// ============================================================================

import type { MessageAttachment } from '../../capabilities/messaging'
import { redactSecrets } from './oauth'
import {
  classifyFacebookError,
  FacebookMessengerError,
  parseRetryAfterMs,
  type FacebookErrorEnvelope,
  type FacebookSendResponse,
} from './types'

// ============================================================================
// Constants
// ============================================================================

const GRAPH_API_BASE = 'https://graph.facebook.com/v25.0'
const MESSAGES_ENDPOINT = `${GRAPH_API_BASE}/me/messages`

// ============================================================================
// Public types
// ============================================================================

export interface SendMessageInput {
  /** Recipient PSID (page-scoped id). */
  to: string
  text?: string
  attachments?: MessageAttachment[]
  /** Optional quick-reply buttons rendered below the message. */
  quickReplies?: Array<{ title: string; payload: string }>
}

export interface SendMessageOutput {
  providerMessageId: string
  sentAt: string
}

export interface SendTypingInput {
  to: string
  state: 'on' | 'off'
}

export interface MarkAsReadInput {
  userId: string
}

// ============================================================================
// sendMessage — strict (throws on error)
// ============================================================================

/**
 * POST a message to the FB Messenger Send API.
 *
 * Throws `FacebookMessengerError` on any non-2xx response. The caller (usually
 * the dispatcher) is expected to catch + retry / surface to the UI.
 */
export async function sendMessage(
  input: SendMessageInput,
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SendMessageOutput> {
  validateRecipient(input.to)
  validateMessagePayload(input)
  assertAccessToken(accessToken)

  const message = buildMessagePayload(input)
  const body = {
    recipient: { id: input.to },
    messaging_type: 'RESPONSE' as const,
    message,
  }

  const response = await fetchImpl(MESSAGES_ENDPOINT, {
    method: 'POST',
    // Page access token goes in the Authorization header — NOT the URL —
    // so Cloudflare edge / proxy logs never see it (FB graph accepts Bearer
    // on every v11+ endpoint).
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw await errorFromResponse(response)
  }

  const parsed = (await response.json()) as FacebookSendResponse
  return {
    providerMessageId: parsed.message_id,
    // FB does not return a server timestamp on success; client-side capture
    // is good enough for dedupe + UI ordering.
    sentAt: new Date().toISOString(),
  }
}

// ============================================================================
// sendTyping — advisory (swallows network errors; still throws INVALID_INPUT)
// ============================================================================

/**
 * Show or hide the typing indicator on the FB conversation.
 *
 * Typing indicators are cosmetic; a failure here must not break the
 * conversation flow. We log + swallow non-2xx + thrown fetch errors but still
 * propagate `INVALID_INPUT` (empty recipient) because that indicates a bug in
 * the caller — silently swallowing it would hide the root cause.
 */
export async function sendTyping(
  input: SendTypingInput,
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  validateRecipient(input.to)
  assertAccessToken(accessToken)
  if (input.state !== 'on' && input.state !== 'off') {
    throw new FacebookMessengerError(
      'INVALID_INPUT',
      `sendTyping: state must be 'on' or 'off', got ${input.state}`,
    )
  }

  const body = {
    recipient: { id: input.to },
    sender_action: input.state === 'on' ? 'typing_on' : 'typing_off',
  }

  await postSenderAction(body, accessToken, fetchImpl, 'sendTyping')
}

// ============================================================================
// markAsRead — advisory (same swallow policy as sendTyping)
// ============================================================================

/**
 * Send a `mark_seen` sender_action for the given user. Not wired into the
 * `messaging` capability yet — exported for the chat module to call once the
 * read-receipt surface lands (P5b). Keep the same soft-failure semantics as
 * the typing indicator.
 */
export async function markAsRead(
  input: MarkAsReadInput,
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  validateRecipient(input.userId)
  assertAccessToken(accessToken)

  const body = {
    recipient: { id: input.userId },
    sender_action: 'mark_seen' as const,
  }

  await postSenderAction(body, accessToken, fetchImpl, 'markAsRead')
}

// ============================================================================
// Internal helpers
// ============================================================================

function buildUrl(endpoint: string, accessToken: string): string {
  const url = new URL(endpoint)
  url.searchParams.set('access_token', accessToken)
  return url.toString()
}

/**
 * Translate our capability-level `MessageAttachment` union into the payload
 * shape the Graph API expects. Adapter concerns live HERE so callers stay
 * provider-agnostic.
 */
interface FacebookAttachment {
  type: 'image' | 'video' | 'audio' | 'file'
  payload: { url: string; is_reusable: boolean }
}

interface FacebookQuickReply {
  content_type: 'text'
  title: string
  payload: string
}

interface FacebookOutboundMessage {
  text?: string
  attachment?: FacebookAttachment
  quick_replies?: FacebookQuickReply[]
}

function buildMessagePayload(input: SendMessageInput): FacebookOutboundMessage {
  const message: FacebookOutboundMessage = {}

  if (input.text && input.text.length > 0) {
    message.text = input.text
  }

  if (input.attachments && input.attachments.length > 0) {
    // Graph API accepts a single `attachment` per message. Pick the first
    // media-type attachment; quick-reply attachments are handled separately.
    const media = input.attachments.find(
      (a): a is Exclude<MessageAttachment, { type: 'quick_replies' }> =>
        a.type !== 'quick_replies',
    )
    if (media) {
      message.attachment = {
        type: media.type,
        payload: { url: media.url, is_reusable: false },
      }
    }

    // Capability-level `quick_replies` attachments promote into the top-level
    // `message.quick_replies`.
    const qr = input.attachments.find(
      (a): a is Extract<MessageAttachment, { type: 'quick_replies' }> =>
        a.type === 'quick_replies',
    )
    if (qr) {
      message.quick_replies = qr.options.map((o) => ({
        content_type: 'text',
        title: o.label,
        payload: o.payload,
      }))
    }
  }

  if (input.quickReplies && input.quickReplies.length > 0) {
    // Explicit `quickReplies` from the SendMessageInput overrides any
    // quick-replies attachment — the explicit field is higher-intent.
    message.quick_replies = input.quickReplies.map((o) => ({
      content_type: 'text',
      title: o.title,
      payload: o.payload,
    }))
  }

  return message
}

async function postSenderAction(
  body: Record<string, unknown>,
  accessToken: string,
  fetchImpl: typeof fetch,
  opLabel: string,
): Promise<void> {
  try {
    const response = await fetchImpl(MESSAGES_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const err = await errorFromResponse(response)
      // Advisory path: log + swallow. Any uplift to "strict" would require
      // plumbing a sink argument through the capability, which is out of
      // scope until the chat module wires a telemetry bus.
      console.error(
        `[facebook-messenger] ${opLabel} failed (swallowed): code=${err.code} httpStatus=${err.details.httpStatus ?? 'n/a'} providerCode=${err.details.providerCode ?? 'n/a'} fbtraceId=${err.details.fbtraceId ?? 'n/a'}`,
      )
    }
  } catch (networkErr) {
    // Propagate INVALID_INPUT (bug on our side) but swallow transport errors.
    if (
      networkErr instanceof FacebookMessengerError &&
      networkErr.code === 'INVALID_INPUT'
    ) {
      throw networkErr
    }
    // Redact any token that might sit inside the thrown error message before
    // hitting Workers tail logs.
    const safeMessage = networkErr instanceof Error
      ? redactSecrets(networkErr.message)
      : 'unknown'
    console.error(
      `[facebook-messenger] ${opLabel} network error (swallowed):`,
      safeMessage,
    )
  }
}

async function errorFromResponse(response: Response): Promise<FacebookMessengerError> {
  let envelope: FacebookErrorEnvelope | null = null
  try {
    envelope = (await response.json()) as FacebookErrorEnvelope
  } catch {
    // FB occasionally returns HTML for edge gateway errors; keep envelope null.
  }
  const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'))
  return classifyFacebookError(envelope, response.status, retryAfterMs)
}

// ============================================================================
// Input validation
// ============================================================================

function validateRecipient(to: string | undefined | null): void {
  if (!to || typeof to !== 'string' || to.trim().length === 0) {
    throw new FacebookMessengerError(
      'INVALID_INPUT',
      'recipient id is required (SendMessageArgs.to / userId)',
    )
  }
}

function validateMessagePayload(input: SendMessageInput): void {
  const hasText = typeof input.text === 'string' && input.text.length > 0
  const hasAttachments = Array.isArray(input.attachments) && input.attachments.length > 0
  const hasQuickReplies = Array.isArray(input.quickReplies) && input.quickReplies.length > 0
  if (!hasText && !hasAttachments && !hasQuickReplies) {
    throw new FacebookMessengerError(
      'INVALID_INPUT',
      'sendMessage: must supply text, attachments, or quickReplies',
    )
  }
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new FacebookMessengerError('INVALID_INPUT', 'accessToken is required')
  }
}
